// Chemin: C:\gestion-copro\src\pages\vigie\AffairesVigiePage.tsx
// Session 125 : Module "Mes dossiers" VIGIE — Page liste
// Principe ESCALATOR : sous-ensemble identique du CS (AffairesPage.tsx)
// Différences VIGIE : 3 statuts (ouverte/en_attente/cloturee), couleur ambre, nommage "dossiers"
// Session 235 : suppression mot "signalement" dans les textes UI
// Session 180 : Badge résolu + compteur "Résolus" distinct + style carte résolue (titre barré, opacité)
//               "Tous" exclut les résolus — même logique que correspondances et calendrier

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';

// Session 136 : Aide contextuelle 3 niveaux
import { useAideContextuelle } from '../../hooks/useAideContextuelle';
import AideContextuelleModule from '../../components/ui/AideContextuelleModule';
import { AIDE_DOSSIERS } from '../../constants/aideContextuelle';
import {
  Affaire,
  StatutAffaire,
  TypeAffaire,
  TYPE_AFFAIRE_LABELS,
  STATUT_AFFAIRE_LABELS,
  STATUT_AFFAIRE_COLORS,
  PRIORITE_AFFAIRE_LABELS,
  PRIORITE_AFFAIRE_COLORS
} from '../../types/affaire.types';
import { getAffairesByCopropriete } from '../../services/affaire.service';
import {
  FolderOpen,
  Plus,
  Search,
  X,
  Eye,
  Edit,
  Loader2,
  AlertCircle,
  Sparkles,
  CheckCheck
} from 'lucide-react';

// ============================================
// CONSTANTES VIGIE
// ============================================

// Session 180 : 4 filtres — ouverte / en_attente / resolus (champ resolu) / cloturee (statut workflow)
type StatutVigieFilter = 'tous' | 'ouverte' | 'en_attente' | 'resolus' | 'cloturee';

// Session 180 : Compteurs enrichis — ajout "Résolus" (champ resolu, distinct de cloturée)
const STATUT_VIGIE_COMPTEURS: { key: StatutVigieFilter; label: string; colorActive: string; colorText: string }[] = [
  { key: 'ouverte', label: 'Ouverts', colorActive: 'border-amber-500 bg-amber-50', colorText: 'text-amber-600' },
  { key: 'en_attente', label: 'En attente', colorActive: 'border-gray-500 bg-gray-50', colorText: 'text-gray-600' },
  { key: 'resolus', label: 'Résolus', colorActive: 'border-green-500 bg-green-50', colorText: 'text-green-600' },
  { key: 'cloturee', label: 'Clôturés', colorActive: 'border-blue-500 bg-blue-50', colorText: 'text-blue-600' },
];

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export default function AffairesVigiePage() {
  const { userProfile } = useAuth();
  const { coproprieteId } = useCopropriete();

  // Session 136 : Aide contextuelle
  const aide = useAideContextuelle({ moduleKey: 'dossiers' });

  const [affaires, setAffaires] = useState<Affaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtres
  const [filtreStatut, setFiltreStatut] = useState<StatutVigieFilter>('tous');
  const [filtreType, setFiltreType] = useState<TypeAffaire | 'tous'>('tous');
  const [recherche, setRecherche] = useState('');

  // Charger les dossiers
  useEffect(() => {
    if (!coproprieteId) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        const affairesData = await getAffairesByCopropriete(coproprieteId);
        setAffaires(affairesData);
      } catch (err) {
        console.error('Erreur chargement dossiers:', err);
        setError('Impossible de charger les dossiers');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [coproprieteId]);

  // Session 180 : Compteurs — "Résolus" = champ resolu:true (indépendant du statut workflow)
  const compteurs: Record<StatutVigieFilter, number> = { tous: 0, ouverte: 0, en_attente: 0, resolus: 0, cloturee: 0 };
  affaires.forEach(a => {
    // Les résolus sont comptés à part, quel que soit le statut workflow
    if ((a as any).resolu === true) {
      compteurs.resolus++;
      return;
    }
    // "en_cours" côté CS est mappé sur "ouverte" côté VIGIE
    if (a.statut === 'en_cours' || a.statut === 'ouverte') {
      compteurs.ouverte++;
    } else if (a.statut === 'en_attente') {
      compteurs.en_attente++;
    } else if (a.statut === 'cloturee') {
      compteurs.cloturee++;
    }
  });

  // Session 180 : Filtrage — "tous" exclut les résolus, "resolus" montre uniquement les résolus
  const affairesFiltrees = affaires.filter(affaire => {
    const isResolu = (affaire as any).resolu === true;

    // Filtre statut VIGIE
    if (filtreStatut === 'resolus') {
      if (!isResolu) return false;
    } else if (filtreStatut === 'tous') {
      // "Tous" = tous les non-résolus
      if (isResolu) return false;
    } else if (filtreStatut === 'ouverte') {
      if (isResolu) return false;
      if (affaire.statut !== 'ouverte' && affaire.statut !== 'en_cours') return false;
    } else if (filtreStatut === 'cloturee') {
      if (isResolu) return false;
      if (affaire.statut !== 'cloturee') return false;
    } else {
      if (isResolu) return false;
      if (affaire.statut !== filtreStatut) return false;
    }

    // Filtre type
    if (filtreType !== 'tous' && affaire.type !== filtreType) return false;

    // Recherche texte
    if (recherche) {
      const searchLower = recherche.toLowerCase();
      return (
        affaire.titre.toLowerCase().includes(searchLower) ||
        affaire.numero.toLowerCase().includes(searchLower) ||
        affaire.description.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  const filtresActifs = [
    filtreStatut !== 'tous' ? filtreStatut : '',
    filtreType !== 'tous' ? filtreType : '',
    recherche
  ].filter(Boolean).length;

  const resetFiltres = () => {
    setFiltreStatut('tous');
    setFiltreType('tous');
    setRecherche('');
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Non renseigné';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return 'Non renseigné';
    return date.toLocaleDateString('fr-FR');
  };

  // Label statut adapté VIGIE
  const getStatutLabelVigie = (statut: StatutAffaire): string => {
    if (statut === 'en_cours') return 'Ouvert'; // Mappé pour VIGIE
    if (statut === 'cloturee') return 'Clôturé';
    return STATUT_AFFAIRE_LABELS[statut];
  };

  // Chargement
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mes dossiers</h1>
          <p className="text-gray-600 mt-1">
            Regroupez vos problèmes, documents et échanges par sujet
          </p>
        </div>
        <Link
          to="/vigie/dossiers/nouveau"
          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nouveau dossier
        </Link>
      </div>

      {/* Erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Session 136 : Aide contextuelle */}
      {aide.aideChargee && (
        <AideContextuelleModule
          config={AIDE_DOSSIERS}
          aideAccueilVue={aide.aideAccueilVue}
          afficherAideComplete={aide.afficherAideComplete}
          onFermer={aide.fermerAideAccueil}
          onVoirPlus={aide.ouvrirAideComplete}
        />
      )}

      {/* Session 180 : Compteurs — 4 statuts VIGIE (ouverts / en attente / résolus / clôturés) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {STATUT_VIGIE_COMPTEURS.map(({ key, label, colorActive, colorText }) => (
          <button
            key={key}
            onClick={() => setFiltreStatut(filtreStatut === key ? 'tous' : key)}
            className={`p-4 rounded-lg border-2 transition-colors ${
              filtreStatut === key ? colorActive : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className={`text-2xl font-bold ${colorText}`}>{compteurs[key]}</div>
            <div className="text-sm text-gray-600">{label}</div>
          </button>
        ))}
      </div>

      {/* Filtres et recherche */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Recherche */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par titre, numéro..."
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
            {recherche && (
              <button
                onClick={() => setRecherche('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filtre type */}
          <select
            value={filtreType}
            onChange={(e) => setFiltreType(e.target.value as TypeAffaire | 'tous')}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          >
            <option value="tous">Tous les types</option>
            {Object.entries(TYPE_AFFAIRE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          {/* Réinitialiser */}
          {filtresActifs > 0 && (
            <button
              onClick={resetFiltres}
              className="text-sm text-amber-600 hover:text-amber-800 flex items-center gap-1 px-3"
            >
              <X className="w-4 h-4" />
              Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* Liste des dossiers */}
      {affairesFiltrees.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          {affaires.length === 0 ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun dossier</h3>
              <p className="text-gray-500 mb-4">
                Créez un dossier pour regrouper vos problèmes, documents et courriers autour d'un même sujet.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/vigie/dossiers/nouveau"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Créer un dossier
                </Link>
                <span className="text-gray-400">ou</span>
                <Link
                  to="/vigie/sentinelle"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors"
                >
                  <Sparkles className="w-5 h-5" />
                  Demander à SENTINELLE
                </Link>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {filtreStatut === 'resolus' ? 'Aucun dossier résolu' : 'Aucun résultat'}
              </h3>
              <p className="text-gray-500 mb-4">
                {filtreStatut === 'resolus'
                  ? 'Les dossiers marqués comme résolus apparaîtront ici.'
                  : 'Aucun dossier ne correspond à vos filtres.'
                }
              </p>
              {filtreStatut !== 'resolus' && (
                <button onClick={resetFiltres} className="text-amber-600 hover:text-amber-800 font-medium">
                  Réinitialiser les filtres
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-100">
            {affairesFiltrees.map((affaire) => {
              const isResolu = (affaire as any).resolu === true;

              return (
                <Link
                  key={affaire.id}
                  to={`/vigie/dossiers/${affaire.id}`}
                  className={`flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors group ${
                    isResolu ? 'opacity-70' : ''
                  }`}
                >
                  {/* Icône type */}
                  <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                    isResolu ? 'bg-green-100' : 'bg-amber-100'
                  }`}>
                    {isResolu
                      ? <CheckCheck className="w-5 h-5 text-green-600" />
                      : <FolderOpen className="w-5 h-5 text-amber-600" />
                    }
                  </div>

                  {/* Infos principales */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Session 180 : Titre barré si résolu */}
                      <span className={`font-medium truncate ${
                        isResolu ? 'text-gray-500 line-through' : 'text-gray-900'
                      }`}>
                        {affaire.titre}
                      </span>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${STATUT_AFFAIRE_COLORS[affaire.statut]}`}>
                        {getStatutLabelVigie(affaire.statut)}
                      </span>
                      {/* Session 180 : Badge résolu vert */}
                      {isResolu && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                          <CheckCheck className="w-3 h-3" />
                          Résolu
                        </span>
                      )}
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${PRIORITE_AFFAIRE_COLORS[affaire.priorite]}`}>
                        {PRIORITE_AFFAIRE_LABELS[affaire.priorite]}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>{affaire.numero}</span>
                      <span className="text-gray-300">·</span>
                      <span>{TYPE_AFFAIRE_LABELS[affaire.type]}</span>
                      <span className="text-gray-300">·</span>
                      <span>{formatDate(affaire.dateOuverture)}</span>
                      {(affaire.signalementIds.length > 0 || affaire.documentIds.length > 0) && (
                        <>
                          <span className="text-gray-300">·</span>
                          <span>
                            {affaire.signalementIds.length} élém. / {affaire.documentIds.length} doc.
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Chevron */}
                  <div className="flex-shrink-0 text-gray-400 group-hover:text-amber-600 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
